import { IsOEPGuid } from '../contracts/ids.js';
import { RunView, type IRunViewProvider, type UserInfo } from '@memberjunction/core';
import type { OEPPhase } from '../workflow/phases.js';
import { IsOEPPhase } from '../workflow/phases.js';
import { OEP_ENTITY, type OEPEntityName } from '../contracts/entities.js';
import type { OEPDraftOrSubmitted, OEPRuleRow, OEPRuleScalar } from './decisions.js';

/** Season context of a presentation; null SeasonID = v1/AGU25 archive row (rules do not apply). */
export interface OEPPresentationContext { PresentationID: string; PresenterUserID: string; SeasonID: string | null; Phase: OEPPhase | null; WinnersPerSection: number }
export type OEPReviewContext = OEPPresentationContext & { ReviewStatus: string; ReviewAssignmentID: string };

/** Every lookup the entity rules need. Default implementation runs RunView as the saving user (RLS-scoped). */
export interface IOEPRuleData {
    /** Fresh DB read of the listed columns of one row; null when absent or not visible to the saver. */
    CurrentRow(entityName: OEPEntityName, id: string, fields: readonly string[]): Promise<OEPRuleRow | null>;
    PresentationForAssignment(assignmentID: string): Promise<OEPPresentationContext | null>;
    PresentationForReview(reviewID: string): Promise<OEPReviewContext | null>;
    Presentation(presentationID: string): Promise<OEPPresentationContext | null>;
    ReviewForConversation(conversationID: string): Promise<{ ReviewID: string } | null>;
    ThreadSenders(conversationID: string): Promise<string[]>;
    ConversationCountForReview(reviewID: string): Promise<number>;
    ReviewCountForAssignment(assignmentID: string): Promise<number>;
    ReviewResponsesComplete(reviewID: string): Promise<boolean>;
    QuestionRange(questionID: string): Promise<{ Min: number | null; Max: number | null }>;
    Nomination(nominationID: string): Promise<{ Status: OEPDraftOrSubmitted; SeasonID: string } | null>;
    NominationResponseSummary(nominationID: string): Promise<{ RatedCount: number; OutstandingWithoutJustification: number }>;
    SeasonPhase(seasonID: string): Promise<{ Phase: OEPPhase; WinnersPerSection: number } | null>;
    Selection(selectionID: string): Promise<{ Status: OEPDraftOrSubmitted; SeasonID: string; SectionID: string } | null>;
    SelectionSlots(selectionID: string): Promise<number[]>;
    SelectionItemCount(selectionID: string): Promise<number>;
    PresentationHasSubmittedNomination(presentationID: string): Promise<boolean>;
    /** Presentation.SectionID (fresh read as the saver); null when unset or not visible. */
    PresentationSectionID(presentationID: string): Promise<string | null>;
}

/**
 * Normalizes a raw column value to OEPRuleScalar. `unknown` because RunView rows and
 * BaseEntity field values are untyped (`any`) at the MJ boundary; this is the one narrowing point.
 */
export function ToRuleScalar(value: unknown): OEPRuleScalar {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    if (value instanceof Date) return value.toISOString();
    return String(value);
}

const COLUMN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export class RunViewOEPRuleData implements IOEPRuleData {
    constructor(private readonly provider: IRunViewProvider, private readonly user: UserInfo) {}

    private async rows(EntityName: string, ExtraFilter: string, Fields: readonly string[], OrderBy?: string): Promise<OEPRuleRow[]> {
        for (const f of Fields) if (!COLUMN.test(f)) throw new Error(`[oep.rules] not a column name: ${f}`);
        // Record<string, unknown>: RunView 'simple' rows are untyped; each value is narrowed by ToRuleScalar.
        const r = await new RunView(this.provider).RunView<Record<string, unknown>>({ EntityName, ExtraFilter, Fields: [...Fields], OrderBy, ResultType: 'simple' }, this.user);
        if (!r.Success) throw new Error(`[oep.rules] ${EntityName} lookup failed: ${r.ErrorMessage}`);
        return r.Results.map((row) => Object.fromEntries(Fields.map((f) => [f, ToRuleScalar(row[f])])));
    }
    private static g(id: string): string {
        if (!IsOEPGuid(id)) throw new Error(`[oep.rules] not a GUID: ${id}`);
        return id;
    }
    private static s(v: OEPRuleScalar | undefined): string {
        return v === null || v === undefined ? '' : String(v);
    }
    async CurrentRow(entityName: OEPEntityName, id: string, fields: readonly string[]): Promise<OEPRuleRow | null> {
        const [row] = await this.rows(entityName, `ID='${RunViewOEPRuleData.g(id)}'`, fields);
        return row ?? null;
    }
    async SeasonPhase(seasonID: string): Promise<{ Phase: OEPPhase; WinnersPerSection: number } | null> {
        const [s] = await this.rows(OEP_ENTITY.Seasons, `ID='${RunViewOEPRuleData.g(seasonID)}'`, ['Phase', 'WinnersPerSection']);
        const phase = s?.['Phase'];
        if (typeof phase !== 'string' || !IsOEPPhase(phase)) return null;
        return { Phase: phase, WinnersPerSection: Number(s['WinnersPerSection']) };
    }
    async Presentation(presentationID: string): Promise<OEPPresentationContext | null> {
        const [p] = await this.rows(OEP_ENTITY.Presentations, `ID='${RunViewOEPRuleData.g(presentationID)}'`, ['ID', 'PresenterUserID', 'SeasonID']);
        if (!p) return null;
        const seasonID = p['SeasonID'] === null ? null : String(p['SeasonID']);
        const season = seasonID ? await this.SeasonPhase(seasonID) : null;
        return { PresentationID: String(p['ID']), PresenterUserID: String(p['PresenterUserID']), SeasonID: seasonID, Phase: season?.Phase ?? null, WinnersPerSection: season?.WinnersPerSection ?? 0 };
    }
    async PresentationForAssignment(assignmentID: string): Promise<OEPPresentationContext | null> {
        const [a] = await this.rows(OEP_ENTITY.ReviewAssignments, `ID='${RunViewOEPRuleData.g(assignmentID)}'`, ['PresentationID']);
        return a ? this.Presentation(String(a['PresentationID'])) : null;
    }
    async PresentationForReview(reviewID: string): Promise<OEPReviewContext | null> {
        const [r] = await this.rows(OEP_ENTITY.Reviews, `ID='${RunViewOEPRuleData.g(reviewID)}'`, ['Status', 'ReviewAssignmentID']);
        if (!r) return null;
        const p = await this.PresentationForAssignment(String(r['ReviewAssignmentID']));
        return p ? { ...p, ReviewStatus: String(r['Status']), ReviewAssignmentID: String(r['ReviewAssignmentID']) } : null;
    }
    async ReviewForConversation(conversationID: string): Promise<{ ReviewID: string } | null> {
        const [c] = await this.rows(OEP_ENTITY.Conversations, `ID='${RunViewOEPRuleData.g(conversationID)}'`, ['ReviewID']);
        return c ? { ReviewID: String(c['ReviewID']) } : null;
    }
    async ThreadSenders(conversationID: string): Promise<string[]> {
        const rows = await this.rows(OEP_ENTITY.ConversationMessages, `ConversationID='${RunViewOEPRuleData.g(conversationID)}' AND ThreadPosition IS NOT NULL`, ['SenderUserID'], 'ThreadPosition');
        return rows.map((r) => String(r['SenderUserID']));
    }
    async ConversationCountForReview(reviewID: string): Promise<number> {
        return (await this.rows(OEP_ENTITY.Conversations, `ReviewID='${RunViewOEPRuleData.g(reviewID)}'`, ['ID'])).length;
    }
    async ReviewCountForAssignment(assignmentID: string): Promise<number> {
        return (await this.rows(OEP_ENTITY.Reviews, `ReviewAssignmentID='${RunViewOEPRuleData.g(assignmentID)}'`, ['ID'])).length;
    }
    async ReviewResponsesComplete(reviewID: string): Promise<boolean> {
        const questions = await this.rows(OEP_ENTITY.ReviewQuestions, `FormType='Review' AND SeasonID IS NULL AND IsRequired=1 AND Status='Active'`, ['ID', 'ReviewQuestionType']);
        const responses = await this.rows(OEP_ENTITY.ReviewResponses, `ReviewID='${RunViewOEPRuleData.g(reviewID)}'`, ['ReviewQuestionID', 'NumericValue', 'TextValue']);
        const byQ = new Map(responses.map((r) => [RunViewOEPRuleData.s(r['ReviewQuestionID']).toUpperCase(), r]));
        return questions.every((q) => {
            const r = byQ.get(RunViewOEPRuleData.s(q['ID']).toUpperCase());
            if (!r) return false;
            const text = typeof r['TextValue'] === 'string' ? r['TextValue'].trim() : '';
            const rated = RunViewOEPRuleData.s(q['ReviewQuestionType']).startsWith('Rating Scale');
            return rated ? r['NumericValue'] !== null && text.length > 0 : text.length > 0;
        });
    }
    async QuestionRange(questionID: string): Promise<{ Min: number | null; Max: number | null }> {
        const [q] = await this.rows(OEP_ENTITY.ReviewQuestions, `ID='${RunViewOEPRuleData.g(questionID)}'`, ['ReviewQuestionTypeID']);
        if (!q) return { Min: null, Max: null };
        const [t] = await this.rows(OEP_ENTITY.ReviewQuestionTypes, `ID='${RunViewOEPRuleData.g(RunViewOEPRuleData.s(q['ReviewQuestionTypeID']))}'`, ['MinValue', 'MaxValue']);
        const n = (v: OEPRuleScalar | undefined): number | null => (v === null || v === undefined ? null : Number(v));
        return { Min: n(t?.['MinValue']), Max: n(t?.['MaxValue']) };
    }
    async Nomination(nominationID: string): Promise<{ Status: OEPDraftOrSubmitted; SeasonID: string } | null> {
        const [n] = await this.rows(OEP_ENTITY.Nominations, `ID='${RunViewOEPRuleData.g(nominationID)}'`, ['Status', 'SeasonID']);
        return n ? { Status: n['Status'] === 'Submitted' ? 'Submitted' : 'Draft', SeasonID: String(n['SeasonID']) } : null;
    }
    async NominationResponseSummary(nominationID: string): Promise<{ RatedCount: number; OutstandingWithoutJustification: number }> {
        const rows = await this.rows(OEP_ENTITY.NominationResponses, `NominationID='${RunViewOEPRuleData.g(nominationID)}' AND NumericValue IS NOT NULL`, ['NumericValue', 'TextValue']);
        return {
            RatedCount: rows.length,
            OutstandingWithoutJustification: rows.filter((r) => Number(r['NumericValue']) === 5 && !(typeof r['TextValue'] === 'string' && r['TextValue'].trim().length > 0)).length,
        };
    }
    async Selection(selectionID: string): Promise<{ Status: OEPDraftOrSubmitted; SeasonID: string; SectionID: string } | null> {
        const [s] = await this.rows(OEP_ENTITY.CommitteeSelections, `ID='${RunViewOEPRuleData.g(selectionID)}'`, ['Status', 'SeasonID', 'SectionID']);
        return s ? { Status: s['Status'] === 'Submitted' ? 'Submitted' : 'Draft', SeasonID: String(s['SeasonID']), SectionID: String(s['SectionID']) } : null;
    }
    async SelectionSlots(selectionID: string): Promise<number[]> {
        return (await this.rows(OEP_ENTITY.CommitteeSelectionItems, `CommitteeSelectionID='${RunViewOEPRuleData.g(selectionID)}'`, ['Slot'])).map((r) => Number(r['Slot']));
    }
    async SelectionItemCount(selectionID: string): Promise<number> {
        return (await this.SelectionSlots(selectionID)).length;
    }
    async PresentationHasSubmittedNomination(presentationID: string): Promise<boolean> {
        return (await this.rows(OEP_ENTITY.Nominations, `PresentationID='${RunViewOEPRuleData.g(presentationID)}' AND Status='Submitted'`, ['ID'])).length > 0;
    }
    async PresentationSectionID(presentationID: string): Promise<string | null> {
        const [p] = await this.rows(OEP_ENTITY.Presentations, `ID='${RunViewOEPRuleData.g(presentationID)}'`, ['SectionID']);
        return p && p['SectionID'] !== null ? String(p['SectionID']) : null;
    }
}

export type OEPRuleDataFactory = (provider: IRunViewProvider, user: UserInfo) => IOEPRuleData;
const defaultFactory: OEPRuleDataFactory = (provider, user) => new RunViewOEPRuleData(provider, user);
let factory: OEPRuleDataFactory = defaultFactory;

/** Test seam: replace the lookup implementation (pass null to restore the RunView default). */
export function SetOEPRuleDataFactory(f: OEPRuleDataFactory | null): void { factory = f ?? defaultFactory; }
export function CreateOEPRuleData(provider: IRunViewProvider, user: UserInfo): IOEPRuleData { return factory(provider, user); }
